/**
 * A set of functions to get links to all of the external pages that are used in the app.
 * It may seem like overkill to have a function for each link, but it makes it easier to
 * change the links later (which is definitely going to happen) without having to search
 * through the codebase for all the links.
 */

// get external links to the various pages (names are likely to change)

// let protocol be set directly for each env
const trainingBaseUrl = window.trainingUrl; // FULL url including protocol
const libraryBaseUrl = window.libraryUrl; // FULL url including protocol
const authoringBaseUrl = window.authoringUrl; 
const insightsUrl = window.insightsUrl; // FULL url including protocol



// const protocol = window.location.protocol; // should be `http` on dev, `https` on staging/prod

/**
 * @returns {string} the endpoint for the the admin training management view
 */
export const getLinkToAdminTrainingManagement = () => {
  return `${trainingBaseUrl}/admin/content-management/studies`;
}

export const getLinkToAuthoring = () => {
  return `${authoringBaseUrl}`;
}

export const getLinkToContentModuleAuthoring = (contentModuleId) => {
  if(!contentModuleId) {
    return null;
  }
  return `${authoringBaseUrl}/content-modules/${contentModuleId}`;
}

export const getLandingPageUrl = (group) => {
  if(!group || !group.slug) {
    return null; 
  }
  const url = window.location.origin + `/groups/landing/${encodeURIComponent(group?.slug)}/${group?._id}`
  return url; 
}

/**
 * 
 * @param {object} group - the group object
 * @returns {string} the link to the user's training page for the group
 */
export const getLinkToTraining = (group) => {
  if(!group || !group.slug) {
    // console.error('getLinkToTraining: no group slug provided');
    return null;
  }
  // we have to encode the slug because it can contain special characters that break the url!
  return `${trainingBaseUrl}/my-agenda/program/${encodeURIComponent(group.slug)}`;
}

export const getLinkToLibrary = (group) => {
  if(!group || !group._id) {
    // console.error('getLinkToLibrary: no group group provided');
    return null;
  }
  return `${libraryBaseUrl}/${group._organization}/${group._id}`;
}

/**
 * 
 * @param {object} group - the group object
 * @returns {string} the link to the group history page
 */
export const getLinkToGroupHistory = (group) => {
  if(!group || !group._id) {
    // console.error('getLinkToGroupHistory: no group id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/audit-logs/study/${group._id}`;
}

export const getLinkToOrgHistory = (organization) => {
  if(!organization || !organization._id) {
    return null;
  }
  return `${trainingBaseUrl}/admin/audit-logs/sponsor/${organization._id}`;
}

/**
 * 
 * @param {object} groupId - the group id
 * @returns {string} the link to the group history page
 */
// TODO: the naming/usage of these needs some attention
export const getLinkToGroupTraining = (groupId) => {
  if(!groupId) {
    // console.error('getLinkToGroupTraining: no group id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/content-management/studies/${groupId}`;
}

/**
 * 
 * @param {object} groupId - the group id
 * @returns {string} the link to the group history page
 */
// TODO: the naming/usage of these needs some attention
export const getLinkToGroupCourse = (groupId, courseId) => {
  if(!groupId || !courseId) {
    // console.error('getLinkToGroupCourse: no group id or course id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/content-management/studies/${groupId}/courses/${courseId}`;
}

/**
 * 
 * @param {object} groupId - the group id
 * @returns {string} the link to the group history page
 */
export const getLinkToGroupSites = (groupId) => {
  if(!groupId) {
    // console.error('getLinkToGroupSites: no group id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/content-management/studies/${groupId}/sites`;
}

/**
 * 
 * @param {object} groupId - the group id
 * @returns {string} the link to the group history page
 */
export const getLinkToGroupRoles = (groupId) => {
  if(!groupId) {
    // console.error('getLinkToGroupRoles: no group id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/content-management/studies/${groupId}/roles`;
}

/**
 * 
 * @param {object} groupUser - the groupUser object
 * @returns {string} the link to the groupUser history page
 */
export const getLinkToGroupUserHistory = (groupUser) => {
  if(!groupUser || !groupUser._id) {
    // console.error('getLinkToGroupHistory: no group id provided');
    return null;
  }
  return `${trainingBaseUrl}/admin/audit-logs/study-user/${groupUser._id}`;
}

/**
 * @returns {string} the endpoint for the the admin user role report (downloadable csv)
 */
export const getLinkToAdminRoleReport = () => {
  return `${trainingBaseUrl}/api/users/role-report`;
}

/**
 * @param userId {string} the id of the user
 * @returns {string} the link to the admin user audit log page
 */
export const getLinkToAdminUserAuditLogs = (userId) => {
  return `${trainingBaseUrl}/admin/audit-logs/user/${userId}`;
}

/**
 * @param {string} userId - the id of the user
 * @returns {string} the endpoint for the user account activity report (downloadable csv)
 */
export const getLinkToAdminUserActivityReport = (userId) => {
  return `${trainingBaseUrl}/api/user-events/report/user-account-activity/${userId}`;
}

export const safeRedirect = (redirectUrl, replace = false) => {
  const redirectStrategy = replace ? 'replace' : 'assign';
  if(isSafeUrl(redirectUrl)) {
    console.log("redirect url is safe", redirectUrl)
    window.location[redirectStrategy](redirectUrl);
  } else {
    console.error("redirectUrl is not safe", redirectUrl);
    // essentially a redirect to the homepage with no params
    window.location[redirectStrategy](window.location.origin);
  }
}

export const getDefaultReportLink = (group) => {
  if(!group || !group._id) {
    // console.error('getLinkToDefaultReport: no group id provided');
    return null;
  }
  const defaultReport = group?.insights?.activeReports?.[0]
  if(!defaultReport) {
    // if there is no default report, link to the reports list
    return getLinkToReportsList(group);
  }
  return `${insightsUrl}/groups/${group._id}/reports/${defaultReport.reportId}`;
}

export const getLinkToSingleReport = (group, reportId) => {
  if(!group || !group._id || !reportId) {
    // console.error('getLinkToSingleReport: invalid parameters');
    return null;
  }
  return `${insightsUrl}/groups/${group._id}/reports/${reportId}`;
}

export const getLinkToReportsList = (group) => {
  if(!group || !group._id) {
    // console.error('getLinkToReportsList: no group id provided');
    return null;
  }
  if(!group?._organization) {
    // console.error('getLinkToReportsList: no organization id provided');
    return null;
  }
  return `/${group._organization}/${group._id}/admin?selectedTab=insights`;
}

/**
 * 
 * @param {object} group - the group object
 * @returns {string} the link to the user's training page for the group
 */
export const getLinkToGroupAgenda = (group) => {
  if (!group || !group._organization) {
    // console.error('getLinkToTraining: no group slug provided');
    return null;
  }
  return `${window.agendaUrl || ''}/${group._organization}/${group._id}`;
}

// a safe url is one that is on the same domain as the app (or localhost if in dev)
// returns false if the url is not safe, otherwise returns the url (a truthy value)
/**
 * 
 * @param {string} url - the url to check
 * @returns {string | false} the url if it is safe, otherwise false
 * 
 * NOTE: This will disallow any redirects to external sites
 * FUTURE: We may want to allow landing page login redirect to take users to clients' site with embedded content that requires login
 */
export const isSafeUrl = (url) => {
  if(!url) {
    return false;
  }
  try {
    const safeUrl = new URL(window.location.origin)
    // create a valid URL object from the passed in url string, pass in our origin as a base to account for relative urls (if url is absolute, this will be ignored)
    // NOTE: this helps prevent XSS attacks because the url will be stringified and re-parsed, which will remove any malicious code
    const targetUrl = new URL(url, window.location.origin)

    if(!targetUrl) {
      return false;
    }

    // full hostname are the same (ie, localhost)
    if(safeUrl.hostname == targetUrl.hostname) {
      return url;
    }
    // highest level domain is the same (ie, .p-f.io or .pro-ficiency.com) return true
    const safeUrlRoot = safeUrl.hostname.split('.').slice(-2).join('.')
    const targetUrlRoot = targetUrl.hostname.split('.').slice(-2).join('.')
    if(safeUrlRoot == targetUrlRoot) {
      return url;
    }

    return false;

  } catch (e) {
    return false;
  }
}
